import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft, Clock, Video, ClipboardList, BookOpen,
  Copy, FileText, CheckCircle2, TrendingUp, Target, ExternalLink,
  BarChart3, ListTodo, MessageSquareText, Inbox
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import { EmptyState } from '../components/ui/EmptyState';
import { useSupabaseClient } from '../hooks/useSupabaseClient';
import { generateParentReportText } from '../utils/parentReport';
import { getAssignmentUrl, formatTurkishDueDate, formatDueDateForSave } from '../utils/assignment';
import { formatStudentName } from '../utils/student';
import { normalizeMeetUrl } from '../utils/meet';
import type { StudentSnapshotRow, AssignmentRow, ParentReportRow, LiveSessionRow } from '../types/sinif';

function formatMs(ms: number) {
  const hours = Math.floor(ms / 3600000);
  const minutes = Math.round((ms % 3600000) / 60000);
  if (hours > 0) return `${hours}s ${minutes}dk`;
  return `${minutes}dk`;
}

export default function StudentDetail() {
  const { classId, studentId } = useParams<{ classId: string; studentId: string }>();
  const { user } = useUser();
  const getSupabase = useSupabaseClient();
  const [snapshot, setSnapshot] = useState<StudentSnapshotRow | null>(null);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [reports, setReports] = useState<ParentReportRow[]>([]);
  const [reportText, setReportText] = useState('');
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [liveSessions, setLiveSessions] = useState<LiveSessionRow[]>([]);
  const [isMeetModalOpen, setIsMeetModalOpen] = useState(false);
  const [inputMeetUrl, setInputMeetUrl] = useState('');
  const [meetError, setMeetError] = useState<string | null>(null);
  const [isStartingMeet, setIsStartingMeet] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Yeni atama formu
  const [type, setType] = useState<'video' | 'odev' | 'test'>('video');
  const [title, setTitle] = useState('');
  const [resourceRef, setResourceRef] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    const supabase = await getSupabase();

    const [{ data: snap }, { data: profile }] = await Promise.all([
      supabase.from('student_snapshots').select('*').eq('student_id', studentId).maybeSingle(),
      supabase.from('profiles').select('full_name').eq('user_id', studentId).maybeSingle(),
    ]);

    const rawName = snap?.display_name && !snap.display_name.startsWith('user_')
      ? snap.display_name
      : profile?.full_name ?? snap?.display_name;

    const resolvedName = formatStudentName(rawName, 'Öğrenci');

    setSnapshot(
      snap
        ? ({ ...snap, display_name: resolvedName } as StudentSnapshotRow)
        : ({
            student_id: studentId,
            display_name: resolvedName,
            last_active_at: null,
            total_study_ms: 0,
            completed_videos: 0,
            total_videos: 0,
            week_study_ms: 0,
            week_completed_videos: 0,
            active_plan_count: 0,
            subject_breakdown: [],
            updated_at: new Date().toISOString(),
          } as StudentSnapshotRow)
    );

    const { data: assignmentRows } = await supabase
      .from('assignments')
      .select('*')
      .eq('student_id', studentId)
      .order('created_at', { ascending: false });
    setAssignments((assignmentRows as AssignmentRow[]) ?? []);

    const { data: reportRows } = await supabase
      .from('parent_reports')
      .select('*')
      .eq('student_id', studentId)
      .order('created_at', { ascending: false });
    setReports((reportRows as ParentReportRow[]) ?? []);

    const { data: sessionRows } = await supabase
      .from('live_sessions')
      .select('*')
      .eq('student_id', studentId)
      .order('created_at', { ascending: false });
    setLiveSessions((sessionRows as LiveSessionRow[]) ?? []);

    setIsLoading(false);
  }, [studentId, getSupabase]);

  async function handleStartGoogleMeet(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !studentId) return;
    setMeetError(null);

    const normalizedUrl = normalizeMeetUrl(inputMeetUrl);
    if (!normalizedUrl) {
      setMeetError('Lütfen geçerli bir Google Meet oda linki veya oda kodu girin.');
      return;
    }

    setIsStartingMeet(true);
    try {
      const supabase = await getSupabase();
      await supabase.from('live_sessions').insert({
        teacher_id: user.id,
        student_id: studentId,
        class_id: classId ?? null,
        meeting_url: normalizedUrl,
        scheduled_at: new Date().toISOString(),
        status: 'planlandi',
      });
      setIsMeetModalOpen(false);
      setInputMeetUrl('');
      load();
    } catch (err) {
      console.error('Google Meet ders başlatılamadı:', err);
      setMeetError('Canlı ders başlatılırken bir hata oluştu.');
    } finally {
      setIsStartingMeet(false);
    }
  }

  async function updateLiveSessionStatus(sessionId: string, status: 'tamamlandi' | 'iptal') {
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('live_sessions').update({ status }).eq('id', sessionId);
      if (error) throw error;
      load();
    } catch (err) {
      console.error('Canlı ders durumu güncellenemedi:', err);
    }
  }

  function generateReport() {
    setReportText(generateParentReportText(snapshot?.display_name ?? 'Öğrenci', snapshot));
  }

  function copyReportText() {
    if (!reportText) return;
    navigator.clipboard.writeText(reportText);
  }

  async function markReportSent() {
    if (!user || !studentId || !reportText.trim()) return;
    setIsSavingReport(true);
    const supabase = await getSupabase();
    const today = new Date();
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    await supabase.from('parent_reports').insert({
      teacher_id: user.id,
      student_id: studentId,
      period_start: weekAgo.toISOString().slice(0, 10),
      period_end: today.toISOString().slice(0, 10),
      summary_text: reportText.trim(),
      sent_at: new Date().toISOString(),
    });
    setIsSavingReport(false);
    load();
  }

  useEffect(() => { load(); }, [load]);

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    if (!user || !studentId || !title.trim()) return;
    setIsSubmitting(true);
    const supabase = await getSupabase();
    const { error } = await supabase.from('assignments').insert({
      teacher_id: user.id,
      student_id: studentId,
      class_id: classId ?? null,
      type,
      title: title.trim(),
      resource_ref: resourceRef.trim() || null,
      due_date: formatDueDateForSave(dueDate),
    });
    if (!error) {
      setTitle('');
      setResourceRef('');
      setDueDate('');
      load();
    }
    setIsSubmitting(false);
  }

  if (isLoading) return <div className="p-10 text-text-muted text-sm animate-pulse">Yükleniyor...</div>;

  const completedAssignments = assignments.filter(a => a.status === 'tamamlandi');
  const pendingAssignments = assignments.filter(a => a.status !== 'tamamlandi');

  return (
    <div className="p-4 md:p-6 lg:p-10 max-w-3xl mx-auto">
      <Helmet>
        <title>{snapshot?.display_name ?? 'Öğrenci'} — Detay Raporu | Bitmiyo Sınıf</title>
        <meta name="description" content="Öğrenci çalışma süreleri, tamamlanan konular ve veli raporu." />
      </Helmet>

      {/* Geri */}
      <Link
        to={`/sinif/${classId}`}
        className="inline-flex items-center gap-1.5 text-text-muted text-sm mb-5 hover:text-accent-600 transition-colors"
      >
        <ArrowLeft size={14} /> Sınıfa dön
      </Link>

      {/* Öğrenci Header */}
      <Card className="p-5 mb-6 flex items-center gap-5">
        <Avatar name={snapshot?.display_name} size={56} />
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-black text-text-primary truncate" style={{ fontFamily: 'var(--font-heading)' }}>
            {snapshot?.display_name ?? 'Öğrenci'}
          </h1>
          {snapshot?.last_active_at ? (
            <p className="text-text-muted text-xs mt-0.5">
              Son aktif: {new Date(snapshot.last_active_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          ) : (
            <p className="text-text-muted text-xs mt-0.5">Henüz giriş yapılmadı</p>
          )}
        </div>
      </Card>

      {/* Stat Grid */}
      <h2 className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-3 flex items-center gap-1.5">
        <TrendingUp size={13} className="text-accent-500" /> Genel Bakış
      </h2>
      {!snapshot ? (
        <div className="mb-8">
          <EmptyState
            icon={Inbox}
            title="Henüz veri yok"
            description="Öğrenci ilk yedeklemesini yapınca ilerleme istatistikleri burada görünecek."
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          <Card className="p-4 hover:-translate-y-0.5 transition-all">
            <Video size={18} className="text-accent-500 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-1">Tamamlanan</p>
            <p className="text-xl font-black text-text-primary">
              {snapshot.completed_videos}
              <span className="text-sm font-bold text-text-muted">/{snapshot.total_videos}</span>
            </p>
          </Card>
          <Card className="p-4 hover:-translate-y-0.5 transition-all">
            <Clock size={18} className="text-text-secondary mb-3" />
            <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-1">Toplam Süre</p>
            <p className="text-xl font-black text-text-primary">{formatMs(snapshot.total_study_ms)}</p>
          </Card>
          <Card className="p-4 hover:-translate-y-0.5 transition-all">
            <TrendingUp size={18} className="text-success-500 mb-3" />
            <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-1">Bu Hafta</p>
            <p className="text-xl font-black text-text-primary">{formatMs(snapshot.week_study_ms)}</p>
          </Card>
          <Card className="p-4 hover:-translate-y-0.5 transition-all">
            <BookOpen size={18} className="text-text-secondary mb-3" />
            <p className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-1">Aktif Plan</p>
            <p className="text-xl font-black text-text-primary">{snapshot.active_plan_count}</p>
          </Card>
        </div>
      )}

      {/* Ders Bazında İlerleme */}
      {snapshot && snapshot.subject_breakdown.length > 0 && (
        <>
          <h2 className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-3 flex items-center gap-1.5">
            <BarChart3 size={13} className="text-accent-500" /> Ders Bazında İlerleme
          </h2>
          <Card className="p-4 pr-6 mb-8">
            <ResponsiveContainer width="100%" height={Math.max(snapshot.subject_breakdown.length * 44, 100)}>
              <BarChart
                data={snapshot.subject_breakdown.map(sb => ({
                  subject: sb.subject,
                  pct: sb.total > 0 ? Math.round((sb.completed / sb.total) * 100) : 0,
                  completed: sb.completed,
                  total: sb.total,
                }))}
                layout="vertical"
                margin={{ left: 0, right: 16, top: 4, bottom: 4 }}
              >
                <XAxis type="number" domain={[0, 100]} hide />
                <YAxis
                  type="category"
                  dataKey="subject"
                  width={110}
                  tick={{ fontSize: 11, fill: 'var(--text-secondary)' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  cursor={{ fill: 'var(--border)', opacity: 0.35 }}
                  contentStyle={{ borderRadius: 12, border: '1px solid var(--border)', fontSize: 12, background: 'var(--bg-surface)' }}
                  formatter={(value, _name, item) => [
                    `${item.payload.completed}/${item.payload.total} video (%${value ?? 0})`,
                    'İlerleme',
                  ]}
                />
                <Bar dataKey="pct" radius={[0, 6, 6, 0]} fill="var(--accent-500)" barSize={14} animationDuration={600} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </>
      )}

      {/* Görev Ata */}
      <h2 className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-3 flex items-center gap-1.5">
        <ListTodo size={13} className="text-accent-500" /> Yeni Görev Ata
      </h2>
      <Card className="p-5 mb-8">
        <form onSubmit={handleAssign} className="flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-1.5">
            <button
              type="button"
              onClick={() => setType('video')}
              className={`p-2 rounded-xl border text-xs font-bold transition-all ${type === 'video' ? 'border-accent-500 bg-accent-100 text-accent-600' : 'border-border text-text-secondary hover:border-border/80'}`}
            >
              Video
            </button>
            <button
              type="button"
              onClick={() => setType('test')}
              className={`p-2 rounded-xl border text-xs font-bold transition-all ${type === 'test' ? 'border-accent-500 bg-accent-100 text-accent-600' : 'border-border text-text-secondary hover:border-border/80'}`}
            >
              Test Ödevi
            </button>
            <button
              type="button"
              onClick={() => setType('odev')}
              className={`p-2 rounded-xl border text-xs font-bold transition-all ${type === 'odev' ? 'border-accent-500 bg-accent-100 text-accent-600' : 'border-border text-text-secondary hover:border-border/80'}`}
            >
              Ödev
            </button>
          </div>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Başlık (ör. Türev — Konu Tekrarı)"
            className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:border-accent-400 transition-colors"
          />
          <input
            value={resourceRef}
            onChange={e => setResourceRef(e.target.value)}
            placeholder={
              type === 'test'
                ? 'Test Kodu veya Linki (ör. X7Y9Z2 veya https://soru.bitmiyo.com.tr/coz/...)'
                : type === 'video'
                ? 'YouTube linki'
                : 'Ödev açıklaması'
            }
            className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:border-accent-400 transition-colors"
          />
          <input
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:border-accent-400 transition-colors"
          />
          <Button type="submit" disabled={isSubmitting || !title.trim()}>
            {isSubmitting ? 'Atanıyor...' : 'Görev Ata'}
          </Button>
        </form>
      </Card>

      {/* Atamalar */}
      <h2 className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-3 flex items-center gap-1.5">
        <ClipboardList size={13} className="text-accent-500" /> Atamalar
      </h2>
      {assignments.length === 0 ? (
        <div className="mb-8">
          <EmptyState icon={ClipboardList} title="Henüz görev atanmadı" description="Yukarıdaki formdan öğrenciye ilk görevini atayabilirsin." />
        </div>
      ) : (
        <div className="flex flex-col gap-2 mb-8">
          {/* Bekleyenler */}
          {pendingAssignments.map((a, i) => {
            const url = getAssignmentUrl(a.type, a.resource_ref);
            const iconColor = a.type === 'video' ? 'text-accent-600' : a.type === 'test' ? 'text-purple-600' : 'text-text-secondary';
            return (
              <Card
                key={a.id}
                className="p-3.5 flex items-center gap-3 animate-fade-in-up"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <div className={`shrink-0 ${iconColor}`}>
                  {a.type === 'video' ? <Video size={20} /> : a.type === 'test' ? <Target size={20} /> : <ClipboardList size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm text-text-primary truncate">{a.title}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {a.due_date && (
                      <p className="text-xs text-text-muted">
                        Teslim: {new Date(a.due_date).toLocaleDateString('tr-TR')}
                      </p>
                    )}
                    {url && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-accent-600 hover:underline flex items-center gap-1 font-semibold"
                      >
                        <ExternalLink size={10} /> {a.type === 'test' ? 'soru.bitmiyo.com.tr' : 'Link'}
                      </a>
                    )}
                  </div>
                </div>
                <Badge variant={a.status === 'devam_ediyor' ? 'accent' : 'neutral'}>
                  {a.type === 'test' ? 'Test Ödevi' : a.status === 'devam_ediyor' ? 'Devam Ediyor' : 'Atandı'}
                </Badge>
              </Card>
            );
          })}
          {/* Tamamlananlar */}
          {completedAssignments.map((a, i) => (
            <Card
              key={a.id}
              className="p-3.5 flex items-center gap-3 opacity-60 animate-fade-in-up"
              style={{ animationDelay: `${(pendingAssignments.length + i) * 40}ms` }}
            >
              <CheckCircle2 size={20} className="text-success-500 shrink-0" />
              <p className="flex-1 text-sm text-text-primary truncate">{a.title}</p>
              <Badge variant="success">Tamamlandı</Badge>
            </Card>
          ))}
        </div>
      )}

      {/* Canlı Ders */}
      <h2 className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-3 flex items-center gap-1.5">
        <Video size={13} className="text-blue-500" /> Google Meet ile Canlı Ders
      </h2>

      {liveSessions.some(s => s.status === 'planlandi') && (
        <Card className="p-4 mb-4 border-emerald-300 dark:border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-blue-500/5 to-transparent shadow-md">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <Video size={24} className="text-emerald-600 dark:text-emerald-400 animate-pulse shrink-0" />
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping" />
                  <p className="font-black text-xs uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                    BİREBİR ÖZEL ETÜT YAYINDA
                  </p>
                </div>
                <p className="text-xs text-text-muted">
                  {snapshot?.display_name ?? 'Öğrenciniz'} katılabilir. Sınıftaki diğer öğrenciler bu dersi görmez.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <a
                href={liveSessions.find(s => s.status === 'planlandi')?.meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5 shadow-sm"
              >
                <ExternalLink size={14} /> Odaya Katıl
              </a>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const activeS = liveSessions.find(s => s.status === 'planlandi');
                  if (activeS) updateLiveSessionStatus(activeS.id, 'tamamlandi');
                }}
                className="text-danger-600 border-danger-200 hover:bg-danger-50 font-bold"
              >
                Dersi Bitir / Kapat
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-5 mb-6 border-blue-200 dark:border-blue-500/20 bg-gradient-to-r from-blue-500/5 via-transparent to-transparent">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
          <div className="flex items-center gap-3">
            <Video size={26} className="text-blue-500 shrink-0" />
            <div>
              <p className="font-bold text-sm text-text-primary">Birebir Google Meet Görüşmesi</p>
              <p className="text-xs text-text-muted mt-0.5">Öğrenciyle anında özel etüt canlı görüşmesi başlatın.</p>
            </div>
          </div>
          <Button
            onClick={() => {
              setIsMeetModalOpen(v => !v);
              setMeetError(null);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white border-0 shadow-md"
          >
            <Video size={16} /> Google Meet ile Ders Başlat
          </Button>
        </div>

        {/* Canlı Ders Formu */}
        {isMeetModalOpen && (
          <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-500/20 bg-surface/80 p-4 rounded-xl">
            <p className="text-xs font-black uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-2 flex items-center gap-2">
              <Video size={16} /> Google Meet Oda Bağlantısını Girin
            </p>
            <p className="text-xs text-text-secondary leading-relaxed mb-3">
              Öğrencinin katılması için Google Meet oda linkini aşağıya ekleyin.
            </p>

            <form onSubmit={handleStartGoogleMeet} className="flex flex-col gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => window.open('https://meet.new', '_blank')}
                  className="bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 font-bold text-xs px-3 py-2 rounded-xl transition-all flex items-center gap-1.5"
                >
                  <ExternalLink size={14} /> 1. Yeni Oda Aç (meet.new)
                </button>
              </div>

              <div>
                <label className="text-xs font-bold text-text-primary block mb-1">
                  2. Google Meet Oda Linki veya Kodu <span className="text-danger-500">*</span>
                </label>
                <input
                  value={inputMeetUrl}
                  onChange={e => {
                    setInputMeetUrl(e.target.value);
                    setMeetError(null);
                  }}
                  placeholder="https://meet.google.com/abc-defg-hij veya abc-defg-hij"
                  className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:border-blue-500 transition-colors font-mono"
                />
              </div>

              {meetError && (
                <p className="text-xs text-danger-500 font-semibold">{meetError}</p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMeetModalOpen(false)}
                >
                  İptal
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={isStartingMeet || !inputMeetUrl.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-white border-0"
                >
                  {isStartingMeet ? 'Başlatılıyor...' : 'Görüşmeyi Başlat ve Öğrenciye Bildir'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {liveSessions.length > 0 && (
          <div className="flex flex-col gap-2 pt-3 border-t border-border mt-3">
            {liveSessions.map(s => (
              <div key={s.id} className="flex items-center justify-between text-xs py-1.5 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-text-primary">
                    {s.scheduled_at ? formatTurkishDueDate(s.scheduled_at) : '—'}
                  </span>
                  <a href={s.meeting_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                    <ExternalLink size={10} /> {s.meeting_url}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={s.status === 'tamamlandi' ? 'success' : 'accent'}>
                    {s.status === 'tamamlandi' ? 'Tamamlandı' : 'Planlandı'}
                  </Badge>
                  {s.status === 'planlandi' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateLiveSessionStatus(s.id, 'tamamlandi')}
                      className="text-danger-600 border-danger-200 hover:bg-danger-50 text-[11px] py-1 px-2"
                    >
                      Dersi Bitir
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Veli Raporu */}
      <h2 className="text-[10px] font-black uppercase tracking-widest text-text-secondary mb-3 flex items-center gap-1.5">
        <MessageSquareText size={13} className="text-accent-500" /> Veli Raporu
      </h2>
      <Card className="p-5 mb-4">
        <p className="text-text-muted text-xs mb-4 leading-relaxed">
          Veli iletişim bilgisi burada tutulmaz. Metni oluştur, kopyala ve kendi kanalından (WhatsApp, telefon vb.) ilet.
        </p>
        {!reportText ? (
          <Button variant="outline" onClick={generateReport} disabled={!snapshot}>
            <FileText size={16} /> Rapor Metni Oluştur
          </Button>
        ) : (
          <div className="flex flex-col gap-3">
            <textarea
              value={reportText}
              onChange={e => setReportText(e.target.value)}
              rows={5}
              className="w-full rounded-xl border border-border bg-surface px-3 py-2.5 text-sm outline-none focus:border-accent-400 resize-none transition-colors"
            />
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={copyReportText}>
                <Copy size={14} /> Kopyala
              </Button>
              <Button variant="outline" size="sm" onClick={generateReport}>
                <FileText size={14} /> Yeniden Oluştur
              </Button>
              <Button size="sm" onClick={markReportSent} disabled={isSavingReport}>
                {isSavingReport ? 'Kaydediliyor...' : 'İlettim Olarak İşaretle'}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Eski Raporlar */}
      {reports.length > 0 && (
        <div className="flex flex-col gap-2 mb-8">
          {reports.map((r, i) => (
            <Card key={r.id} className="p-4 animate-fade-in-up" style={{ animationDelay: `${i * 40}ms` }}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-text-secondary">
                  {new Date(r.period_start).toLocaleDateString('tr-TR')} – {new Date(r.period_end).toLocaleDateString('tr-TR')}
                </p>
                {r.sent_at && <Badge variant="success">İletildi</Badge>}
              </div>
              <p className="text-sm text-text-primary line-clamp-2">{r.summary_text}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
